import { useTranslation } from "react-i18next";
import {
  useAssetClasses,
  useReferenceValueLabel,
  useRiskProfiles,
  useTimeframes,
} from "../../../api/hooks/useReference";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";
import { LabelledFact } from "./TradingStrategyInsights";
import { AltSymbolIcon, DirectionIcon, RiskIcon } from "@scryerlabs/ui";
import { displayTicker } from "../tickers";

/**
 * What a strategy trades and which way, as facts rather than as marks alone.
 *
 * The cards carry these in the identity band, where a grid is scanned and an icon is the right
 * density. A detail page is read, there is no hover on a phone, and it is where someone decides
 * whether to subscribe - so here the same three marks are spelled out beside their words.
 *
 * Every one of them is absent-able. Direction and the legs are missing on any strategy imported
 * before those fields existed, and absent must render as nothing rather than as a negative.
 */
export function StrategyTraitFacts({
  strategy,
  showDirection = true,
  showMarket = false,
}: {
  strategy: TradingStrategyDto;
  /** Off where side tabs already name the side. */
  showDirection?: boolean;
  /** Timeframe and asset class as facts, for a page that shows them here rather than as pills. */
  showMarket?: boolean;
}) {
  const { t } = useTranslation("tradingStrategies");
  const riskProfileLabel = useReferenceValueLabel(useRiskProfiles().data);
  const timeframeLabel = useReferenceValueLabel(useTimeframes().data);
  const assetClassLabel = useReferenceValueLabel(useAssetClasses().data);

  const direction = showDirection ? (strategy.direction ?? 0) : 0;
  const legs = [strategy.bullSymbol, strategy.bearSymbol].filter(Boolean) as string[];
  const underlying = displayTicker(strategy.chartedSymbol);

  return (
    <>
      {underlying && (
        <div>
          <LabelledFact label="Charted on" value={underlying} mono />
        </div>
      )}

      {direction > 0 && (
        <div>
          <LabelledFact
            label="Direction"
            value={
              <span className="d-inline-flex align-items-center gap-2">
                <DirectionIcon
                  direction={direction}
                  label={t(`traits.direction.${direction}`)}
                />
                {t(`sides.${direction}`)}
              </span>
            }
          />
        </div>
      )}

      {legs.length > 0 && (
        <div>
          <LabelledFact
            label="Traded through"
            value={
              <span className="d-inline-flex align-items-center gap-2">
                <AltSymbolIcon label={t("traits.altSymbols")} />
                <span className="font-monospace">
                  {strategy.bullSymbol && (
                    <span style={{ color: "var(--sl-positive)" }}>{displayTicker(strategy.bullSymbol)}</span>
                  )}
                  {strategy.bullSymbol && strategy.bearSymbol && (
                    <span className="text-body-secondary fw-normal"> / </span>
                  )}
                  {strategy.bearSymbol && (
                    <span style={{ color: "var(--sl-negative)" }}>{displayTicker(strategy.bearSymbol)}</span>
                  )}
                </span>
              </span>
            }
          />
        </div>
      )}

      <div>
        <LabelledFact
          label="Risk profile"
          value={
            <span className="d-inline-flex align-items-center gap-2">
              <RiskIcon
                riskProfile={strategy.riskProfile}
                label={t("traits.risk", { level: riskProfileLabel(strategy.riskProfile) })}
              />
              {riskProfileLabel(strategy.riskProfile)}
            </span>
          }
        />
      </div>

      {showMarket && (
        <>
          <div>
            <LabelledFact label="Timeframe" value={timeframeLabel(strategy.timeframe)} />
          </div>
          <div>
            <LabelledFact label="Asset class" value={assetClassLabel(strategy.assetClass)} />
          </div>
        </>
      )}
    </>
  );
}
